import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import styled from "styled-components";
import {
  TablePanel,
  Input,
  Button,
  Select,
  UserAvatar,
  RankBadge,
} from "components/common";
import { countryStates, statesByCountry } from "common/country";
import { varKey } from "common/var";
import { callGetApiWithAuth } from "utils/api";
import { asVolume } from "utils/text";
import { ReactComponent as SearchOutlinedIcon } from "assets/icons/search-icon.svg";

function rankLabel(rank) {
  if (!rank) return "";
  if (typeof rank === "string") return rank;
  return rank.name || "";
}

function toPipeRange(min, max) {
  if (min === "" && max === "") return "";
  return `${min ?? ""}|${max ?? ""}`;
}

function ReportTabs() {
  return (
    <TabStrip>
      <NavLink to="/report/current_promoter_rank" activeClassName="tabActive">
        Current Promoter
      </NavLink>
      <NavLink to="/report/current_builder_rank" activeClassName="tabActive">
        Current Builder
      </NavLink>
    </TabStrip>
  );
}

export default function CurrentRankReportView({ variant }) {
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [promoterRanks, setPromoterRanks] = useState([]);
  const [builderRanks, setBuilderRanks] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [paginationParam, setPaginationParam] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });
  const [searchParam, setSearchParam] = useState({
    uuid: "",
    promoter_rank_min: "",
    promoter_rank_max: "",
    builder_rank_min: "",
    builder_rank_max: "",
    country: "",
    state: "",
  });

  const countryOptions = useMemo(
    () => [
      { label: "Country", value: "" },
      ...countryStates.map((c) => ({ label: c.name, value: c.code2 })),
    ],
    []
  );

  const stateOptions = useMemo(() => {
    if (!searchParam.country) {
      return [{ label: "State", value: "" }];
    }
    const states = statesByCountry(searchParam.country) || [];
    return [
      { label: "State", value: "" },
      ...states.map((s) => ({ label: s.name, value: s.code })),
    ];
  }, [searchParam.country]);

  useEffect(() => {
    callGetApiWithAuth("common/ranks", (res) => setPromoterRanks(res.data || []));
    callGetApiWithAuth("common/builder_ranks", (res) =>
      setBuilderRanks(res.data || [])
    );
  }, []);

  const listPath =
    variant === "promoter"
      ? "report/rank_report/current_promoter_reports"
      : "report/rank_report/current_builder_reports";

  const exportPath = 
    variant === "promoter"
      ? "report/rank_report/export_current_promoter_reports"
      : "report/rank_report/export_current_builder_reports";  
  const searchFiltersToParams = (searchParam0) => ({
    uuid: searchParam0.uuid,
    rank_range: toPipeRange(
      searchParam0.promoter_rank_min,
      searchParam0.promoter_rank_max
    ),
    builder_rank_range: toPipeRange(
      searchParam0.builder_rank_min,
      searchParam0.builder_rank_max
    ),
    country: searchParam0.country,
    state: searchParam0.state,
  });

  const onFormInput = (field, value) => {
    if (field === "country") {
      setSearchParam({
        ...searchParam,
        country: value,
        state: "",
      });
      return;
    }
    setSearchParam({
      ...searchParam,
      [field]: value,
    });
  };

  const loadTableData = (paginationParam0, searchParam0) => {
    setIsLoading(true);
    const params = {
      ...searchFiltersToParams(searchParam0),
      page: paginationParam0.currentPage,
      per_page: paginationParam0.perPage,
    };
    const q = Object.keys(params)
      .filter((k) => {
        const v = params[k];
        if (k === "page" || k === "per_page") return true;
        return v !== "" && v != null;
      })
      .map((k) => k + "=" + (params[k] ?? ""))
      .join("&");
    callGetApiWithAuth(
      `${listPath}?${q}`,
      (res) => {
        const page = res.data;
        setTableData(page.data || []);
        setPaginationParam((prev) => ({
          ...prev,
          currentPage: page.current_page,
          total: page.total,
        }));
        setIsLoading(false);
      },
      () => setIsLoading(false)
    );
  };

  const handleSearch = () => {
    const paginationParam0 = {
      ...paginationParam,
      currentPage: 1,
    };
    loadTableData(paginationParam0, searchParam);
  };

  const onPageChange = (page) => {
    const paginationParam0 = {
      ...paginationParam,
      currentPage: page,
    };
    loadTableData(paginationParam0, searchParam);
  };

  useEffect(() => {
    handleSearch();
  }, [variant]);

  const onExportDone = (res) => {
    setIsExporting(false);
    window.open(res.data.csv_file, "_blank");
  };
  const onExportFail = () => {
    setIsExporting(false);
  };

  const handleExport = () => {
    setIsExporting(true);
    const params = searchFiltersToParams(searchParam);
    const q = Object.keys(params)
      .filter((k) => {
        const v = params[k];
        return v !== "" && v != null;
      })
      .map((k) => k + "=" + (params[k] ?? ""))
      .join("&");
    callGetApiWithAuth(
      `${exportPath}?${q}`,
      onExportDone,
      onExportFail
    );
  };

  const rowUser = (record) => record.user || record;

  return (
    <Wrapper>
      <TablePanel
        headerTabs={<ReportTabs />}
        data={tableData}
        loading={isLoading}
        paginationParam={paginationParam}
        onPageChange={onPageChange}
        toolbar={
          <Toolbar>
            <div className="userIdSearch">
              <Input
                value={searchParam.uuid}
                onChange={(e) => onFormInput("uuid", e.target.value)}
                placeholder="User ID"
                aria-label="User ID"
                inputPrefix={<SearchOutlinedIcon />}
              />
            </div>
            <Select
              label="Current Promoter Rank Min"
              value={searchParam.promoter_rank_min}
              onChange={(v) => onFormInput("promoter_rank_min", v)}
              options={[
                { label: "Min", value: "" },
                ...promoterRanks.map((el) => ({
                  label: el.name,
                  value: el.id,
                })),
              ]}
            />
            <Select
              label="Current Promoter Rank Max"
              value={searchParam.promoter_rank_max}
              onChange={(v) => onFormInput("promoter_rank_max", v)}
              options={[
                { label: "Max", value: "" },
                ...promoterRanks.map((el) => ({
                  label: el.name,
                  value: el.id,
                })),
              ]}
            />
            <Select
              label="Current Builder Rank Min"
              value={searchParam.builder_rank_min}
              onChange={(v) => onFormInput("builder_rank_min", v)}
              options={[
                { label: "Min", value: "" },
                ...builderRanks.map((el) => ({
                  label: el.name,
                  value: el.id,
                })),
              ]}
            />
            <Select
              label="Current Builder Rank Max"
              value={searchParam.builder_rank_max}
              onChange={(v) => onFormInput("builder_rank_max", v)}
              options={[
                { label: "Max", value: "" },
                ...builderRanks.map((el) => ({
                  label: el.name,
                  value: el.id,
                })),
              ]}
            />
            <Select
              label="Country"
              value={searchParam.country}
              onChange={(v) => onFormInput("country", v)}
              options={countryOptions}
            />
            <Select
              label="State"
              value={searchParam.state}
              onChange={(v) => onFormInput("state", v)}
              options={stateOptions}
            />
            <Button loading={isLoading} onClick={handleSearch}>
              Search
            </Button>
            <Button loading={isExporting} onClick={handleExport}>
              Export
            </Button>
          </Toolbar>
        }
        columns={[
          {
            title: "User ID",
            key: "uuid",
            render: (_, record) => {
              const u = rowUser(record);
              return <span className="idCell">{u.uuid ?? u.id}</span>;
            },
          },
          {
            title: "Name",
            key: "name",
            render: (_, record) => {
              const u = rowUser(record);
              return (
                <UserAvatar
                  image={u.image}
                  title={`${u.first_name || ""} ${u.last_name || ""}`.trim()}
                  status={u.status != null ? varKey("user.status", u.status) : undefined}
                />
              );
            },
          },
          {
            title: "Current Promoter Rank",
            key: "cur_pr",
            render: (_, record) => (
              <RankBadge label={rankLabel(record.rank)} />
            ),
          },
          {
            title: "Next Promoter Rank",
            key: "next_pr",
            render: (_, record) => (
              <span>{rankLabel(record.next_rank)}</span>
            ),
          },
          {
            title: "Pay Promoter Rank",
            key: "pay_pr",
            render: (_, record) => (
              <RankBadge label={rankLabel(record.pay_rank)} />
            ),
          },
          {
            title: "Highest Promoter Rank",
            key: "hi_pr",
            render: (_, record) => (
              <RankBadge label={rankLabel(record.max_rank)} />
            ),
          },
          {
            title: "Current Builder Rank",
            key: "cur_br",
            render: (_, record) => (
              <RankBadge label={rankLabel(record.builder_rank)} />
            ),
          },
          {
            title: "Next Builder Rank",
            key: "next_br",
            render: (_, record) => (
              <span>{rankLabel(record.next_builder_rank)}</span>
            ),
          },
          {
            title: "Pay Builder Rank",
            key: "pay_br",
            render: (_, record) => (
              <RankBadge label={rankLabel(record.pay_builder_rank)} />
            ),
          },
          {
            title: "Highest Builder Rank",
            key: "hi_br",
            render: (_, record) => (
              <RankBadge label={rankLabel(record.max_builder_rank)} />
            ),
          },
          {
            title: "MP",
            key: "mp",
            render: (_, record) => <span>{asVolume(record.qualification.mp)}</span>,
          },
          {
            title: "AP",
            key: "ap",
            render: (_, record) => <span>{asVolume(record.qualification.ap)}</span>,
          },
          {
            title: "CP",
            key: "cp",
            render: (_, record) => <span>{asVolume(record.qualification.cp)}</span>,
          },
          {
            title: "CP to next rank",
            key: "cp_to_next_rank",
            render: (_, record) => <span>{asVolume(record.next_rank?.setting?.cp)}</span>,
          },
          {
            title: "OP",
            key: "op",
            render: (_, record) => <span>{asVolume(record.qualification.op)}</span>,
          },
          {
            title: "QOP",
            key: "qop",
            render: (_, record) => <span>{asVolume(record.qualification.qop)}</span>,
          },
        ]}
      />
    </Wrapper>
  );
}

const TabStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: center;
  a {
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    color: #637381;
    text-decoration: none;
    padding-bottom: 8px;
    border-bottom: 2px solid transparent;
  }
  a.tabActive {
    color: #212b36;
    font-weight: 600;
    border-bottom-color: #0abab5;
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  width: 100%;
  .userIdSearch {
    min-width: 200px;
    flex: 0 0 auto;
    .input-prefix-container .input-prefix {
      top: 50%;
      transform: translateY(-50%);
      left: 14px;
      z-index: 1;
    }
    .input-prefix-container .ant-input {
      padding-left: 44px;
    }
  }
  .ant-select-selector {
    min-width: 160px !important;
  }
`;

const Wrapper = styled.div`
  padding-bottom: 80px;
  .idCell {
    color: #0abab5;
    font-weight: 500;
    cursor: default;
  }
  .panelHead {
    flex-direction: column;
    align-items: flex-start !important;
  }
  .toolbar {
    width: 100%;
  }
`;
